import React, { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Menu } from "primereact/menu";
import { Tag } from "primereact/tag";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { paginatorTemplate } from "../../components/OtherComponents/PaginatorTemplate";
import { getCoupons, deleteMultipleCoupons } from "../../store/AsyncMethods/CouponMethod";
import InputFocus from "../../hooks/InputFocus";

export default function CouponsTable() {
  const { coupons } = useSelector((state) => state.CouponReducer);
  const menu = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = InputFocus();

  const [showMultipleDelDialog, setShowMultipleDelDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState(null);

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
  };

  const acceptMultipleDelete = () => {
    if (selectedRows && selectedRows.length > 0) {
      const ids = selectedRows.map((r) => r.id);
      dispatch(deleteMultipleCoupons(ids)).then((success) => {
        if (success) {
          setSelectedRows(null);
          dispatch(getCoupons());
        }
      });
    } else {
      toast.info("No record selected.");
    }
  };

  const statusBodyTemplate = (rowData) => (
    <Tag
      value={rowData.is_active ? "Active" : "Inactive"}
      pt={{ root: { className: rowData.is_active ? "bg-green" : "bg-red" } }}
    />
  );

  const discountBodyTemplate = (rowData) => {
    if (rowData.discount_type === "percentage") {
      return `${rowData.discount_value}%`;
    }
    return `PKR ${parseFloat(rowData.discount_value).toLocaleString()}`;
  };

  const expiryBodyTemplate = (rowData) => {
    if (!rowData.expiry_date) return "No expiry";
    // "YYYY-MM-DD" from the API → DD/MM/YYYY, without Date/timezone conversion
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(rowData.expiry_date));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(rowData.expiry_date);
  };

  const usageBodyTemplate = (rowData) => {
    const limit = rowData.usage_limit !== null ? rowData.usage_limit : "∞";
    return `${rowData.used_count} / ${limit}`;
  };

  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => navigate(`/edit-coupon/${selectedItem?.id}`),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        if (selectedItem) {
          dispatch(deleteMultipleCoupons([selectedItem.id])).then((success) => {
            if (success) dispatch(getCoupons());
          });
        }
      },
    },
  ];

  const actionBtn = (rowData) => (
    <i
      className="pi pi-cog text-secondary cursor-pointer"
      onClick={(e) => {
        menu.current.toggle(e);
        setSelectedItem(rowData);
      }}
    />
  );

  const paginatorLeft = (
    <div className="mr-auto">
      <Button
        label="Delete"
        icon="pi pi-trash"
        className="p-red-btn !mr-auto"
        onClick={() => setShowMultipleDelDialog(true)}
        disabled={!selectedRows || selectedRows.length === 0}
      />
    </div>
  );

  const header = (
    <div className="flex justify-between">
      <span className="p-input-icon-left flex w-10/12 sm:w-8/12 md:w-6/12 lg:w-4/12 xl:w-3/12">
        <InputText
          ref={inputRef}
          className="w-full p-primary-input"
          type="search"
          value={filters["global"].value || ""}
          onChange={onGlobalFilterChange}
          placeholder="Search"
        />
      </span>
    </div>
  );

  return (
    <div>
      <div className="card shadow-sm">
        <DataTable
          value={coupons}
          tableStyle={{ minWidth: "50rem" }}
          stripedRows
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          header={header}
          filters={filters}
          onFilter={(e) => setFilters(e.filters)}
          paginatorTemplate={paginatorTemplate}
          selection={selectedRows}
          onSelectionChange={(e) => setSelectedRows(e.value)}
          paginatorLeft={paginatorLeft}
          onRowClick={(e) => navigate(`/edit-coupon/${e.data.id}`)}
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
          <Column field="code" sortable header="Code" />
          <Column field="discount_type" sortable header="Type" body={(r) => r.discount_type === "percentage" ? "Percentage" : "Fixed"} />
          <Column header="Discount" body={discountBodyTemplate} />
          <Column field="min_order_amount" sortable header="Min Order (PKR)" body={(r) => `PKR ${parseFloat(r.min_order_amount || 0).toLocaleString()}`} />
          <Column header="Usage" body={usageBodyTemplate} />
          <Column header="Expiry" body={expiryBodyTemplate} />
          <Column header="Status" body={statusBodyTemplate} />
          <Column header="Action" body={(rowData) => (
            <>
              {actionBtn(rowData)}
              <Menu model={menuItems} popup ref={menu} className="p-0 z-50" />
            </>
          )} />
        </DataTable>
      </div>

      <ConfirmDialog
        message={`Are you sure you want to delete ${selectedRows?.length || 0} coupon(s)?`}
        header="Delete confirmation"
        icon="pi pi-info-circle"
        accept={acceptMultipleDelete}
        reject={() => setShowMultipleDelDialog(false)}
        acceptLabel="Yes"
        visible={showMultipleDelDialog}
        onHide={() => setShowMultipleDelDialog(false)}
        pt={{
          acceptButton: { className: "p-red-btn" },
          rejectButton: { className: "p-primary-btn" },
          root: { className: "w-9/12 sm:w-7/12 md:w-5/12 lg:w-4/12 xl:w-3/12" },
          icon: { className: "mx-2" },
        }}
      />
    </div>
  );
}
